import './styles/tailwind.css';
import './styles/main.scss';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { componentRegistry } from './react/registry';

const THEME_NAME = 'CIL WordPress Theme';

type FeatureToggle = {
  element: HTMLElement;
  activeClass: string;
};

const featureToggles: FeatureToggle[] = [];

function registerFeatureToggle(selector: string, activeClass = 'is-active') {
  const element = document.querySelector<HTMLElement>(selector);
  if (!element) {
    return;
  }

  featureToggles.push({ element, activeClass });
}

function applyFeatureFlags() {
  featureToggles.forEach(({ element, activeClass }) => {
    element.classList.add(activeClass);
    element.dataset.themeLoaded = THEME_NAME;
  });
}

function hydrateThemeMetadata() {
  document.documentElement.dataset.theme = THEME_NAME;
  const banner = document.querySelector<HTMLElement>('[data-theme-banner]');
  if (banner) {
    banner.textContent = `${THEME_NAME} assets loaded`;
  }
}

registerFeatureToggle('.hero-banner');
registerFeatureToggle('.nav-primary', 'is-enhanced');

function initNavToggle() {
  const toggle = document.querySelector<HTMLButtonElement>('.nav-toggle');
  const nav = document.querySelector<HTMLElement>('.nav-primary');
  if (!toggle || !nav) {
    return;
  }

  toggle.addEventListener('click', () => {
    const nextExpanded = toggle.getAttribute('aria-expanded') !== 'true';
    toggle.setAttribute('aria-expanded', nextExpanded ? 'true' : 'false');
    nav.classList.toggle('is-enhanced', nextExpanded);
  });
}

/**
 * Footer toggle - exact replica of React useState toggle behavior
 * Swaps between ArrowDown2 and ArrowUp2 icons (not rotating)
 */
function initFooterToggles() {
  document.querySelectorAll<HTMLButtonElement>('[data-footer-toggle]').forEach((button) => {
    const targetId = button.dataset.footerToggle;
    if (!targetId) {
      return;
    }
    const target = document.getElementById(targetId);
    if (!target) {
      return;
    }

    const arrowDown = button.querySelector<SVGElement>('.footer-arrow-down');
    const arrowUp = button.querySelector<SVGElement>('.footer-arrow-up');

    const setState = (open: boolean) => {
      button.setAttribute('aria-expanded', open ? 'true' : 'false');
      
      if (open) {
        // Show content
        target.classList.remove('hidden');
        target.classList.add('flex');
        // Swap icons: hide down arrow, show up arrow
        if (arrowDown) arrowDown.classList.add('hidden');
        if (arrowUp) arrowUp.classList.remove('hidden');
      } else {
        // Hide content
        target.classList.add('hidden');
        target.classList.remove('flex');
        // Swap icons: show down arrow, hide up arrow
        if (arrowDown) arrowDown.classList.remove('hidden');
        if (arrowUp) arrowUp.classList.add('hidden');
      }
    };

    // Initialize to closed state (matching React's useState(false))
    setState(false);

    button.addEventListener('click', () => {
      const isOpen = button.getAttribute('aria-expanded') === 'true';
      setState(!isOpen);
    });
  });
}

hydrateThemeMetadata();
applyFeatureFlags();
initNavToggle();
initFooterToggles();

// Init converted header
import { initHeader } from './scripts/header';
initHeader();

document.querySelectorAll<HTMLElement>('[data-react-component]').forEach((node) => {
  const name = node.dataset.reactComponent ?? '';
  const Component = componentRegistry[name];
  if (!Component) {
    return;
  }

  let props: Record<string, unknown> = {};
  if (node.dataset.props) {
    try {
      props = JSON.parse(node.dataset.props);
    } catch {
      props = {};
    }
  }

  createRoot(node).render(<Component {...props} />);
});
